import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useState } from 'react';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { convertDateToLocalDateDTO, convertLocalDateDTOToDate } from '../../../../utils/dateUtil';
import PetHealthyLastSixMonths from '../../../../shared_components/PetHealthyLastSixMonths/PetHealthyLastSixMonths';
import type { YesNo_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { DateField, TextField } from '@flos/react-ui';
import { track } from '../../../../utils/trackingUtil';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time';

const PetHealthFor6MonthsStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const pets =
        currentProduct.flavourName === 'Hundeforsikring'
            ? coverables.dogs ?? []
            : coverables.cats ?? [];

    const coverableIndex = getCoverableIndex(pets, currentProduct.fixedId);
    const coverable = pets[coverableIndex];
    const { isHealthyFor6Months, name, dateOfInjury } = coverable;
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [injuryType, setInjuryType] = useState<string>(coverable.typeOfInjury ?? '');
    const [choice, setChoice] = useState<string>('');
    const isValid =
        isHealthyFor6Months === 'Yes' ||
        (isHealthyFor6Months === 'No' && !!coverable.typeOfInjury && !!coverable.dateOfInjury);
    const dateSixMonthsAgo = new Date();
    dateSixMonthsAgo.setMonth(dateSixMonthsAgo.getMonth() - 6);
    dateSixMonthsAgo.setDate(dateSixMonthsAgo.getDate() + 1);

    const handleChoiceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setChoice(value);
        if (value === 'Yes') {
            setInjuryType('');
            updateCoverableInformation({
                isHealthyFor6Months: value,
                typeOfInjury: undefined,
                dateOfInjury: undefined
            });
        } else {
            updateCoverableInformation({
                isHealthyFor6Months: value
            });
        }
    };

    const handleInputInjuryType = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setInjuryType(value);
    };

    const handleInjuryTypeBlur = (): void => {
        updateCoverableInformation({
            typeOfInjury: injuryType
        });
    };

    const handleDate = (date: Date): void => {
        updateCoverableInformation({
            dateOfInjury: convertDateToLocalDateDTO(date)
        });
    };

    const onNext = (): void => {
        setIsLoading(true);

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [currentProduct.flavourName === 'Hundeforsikring' ? 'dog' : 'cat']: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                isHealthyFor6Months,
                dateOfInjury,
                typeOfInjury: coverable.typeOfInjury
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            setIsLoading(false);
            trackHealthyFor6Months();
            goNext();
        });
    };

    const trackHealthyFor6Months = (): void => {
        let trackingData: Array<{
            isHealthyFor6Months?: string;
            dateOfInjury?: LocalDateDTO;
            typeOfInjury?: string;
        }> = [];

        if (isHealthyFor6Months === 'Yes') {
            trackingData = [{ isHealthyFor6Months }];
        } else {
            trackingData = [{ isHealthyFor6Months }, { dateOfInjury }];
        }

        const petType = currentProduct.flavourName === 'Hundeforsikring' ? 'Dog' : 'Cat';
        track(
            `${coverable.flavourName}CoverableInformation${petType}HealthyFor6Months`,
            trackingData
        );
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!isValid || isLoading || isRequestPending}
            disablePrevious={isLoading || isRequestPending}
            isNextLoading={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-PET-COVERABLE-INFORMATION-SIX-MONTHS-HEALTHY-QUESTION', {
                    navn: name ?? ''
                })}
            >
                <PetHealthyLastSixMonths
                    healthValue={isHealthyFor6Months ?? (choice as YesNo_Top)}
                    handleHealthValueChange={handleChoiceChange}
                    disabled={isLoading || isRequestPending}
                />
                <>
                    {isHealthyFor6Months === 'No' && (
                        <>
                            <h4>
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-INJURY-DATE-QUESTION')}
                            </h4>
                            <DateField
                                label="Vælg dato"
                                onChange={handleDate}
                                selected={
                                    dateOfInjury
                                        ? convertLocalDateDTOToDate(dateOfInjury)
                                        : undefined
                                }
                                minDate={dateSixMonthsAgo}
                                maxDate={new Date()}
                                disabled={isLoading || isRequestPending}
                            />
                            <h4>
                                {getHippoText('DSS-PET-COVERABLE-INFORMATION-INJURY-INFO-QUESTION')}
                            </h4>
                            <TextField
                                label="Beskriv skaden"
                                value={injuryType}
                                onChange={handleInputInjuryType}
                                onBlur={handleInjuryTypeBlur}
                                minLength={1}
                                disabled={isLoading || isRequestPending}
                            />
                        </>
                    )}
                </>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default PetHealthFor6MonthsStep;
